import { Position } from '@xyflow/react';

// eslint-disable-next-line import/no-cycle
import {
  generatePosition8P,
  generatePosition10P,
  generatePosition16P,
  generatePosition24P,
} from './node-generate-pos';

export enum EPortType {
  TARGET = 'target',
  SOURCE = 'source',
}

export enum EPortConnectionType {
  POWER = 'power',
  USB = 'usb',
  FE = 'fe',
  GE = 'ge',
  MIXED = 'mixed',
}

export enum EEdgeTypes {
  DEFAULT = 'default',
  STEP = 'step',
  SMOOTHSTEP = 'smoothstep',
  STRAIGHT = 'straight',
  CUSTOM = 'custom',
}
export interface IEdgeData {
  id: string;
  nodeId: string;
  label?: string;
  animated?: boolean;
  type?: EEdgeTypes;
  connectionType?: EPortConnectionType;
  isPoE?: boolean;
}
export interface IPort {
  id: string;
  type: EPortType[];
  isPoE?: boolean;
  connectionType: EPortConnectionType;
  edgeData?: Omit<IEdgeData, 'id' | 'nodeId'>;
  position: Position;
  style?: React.CSSProperties;
}

export interface IpInterface {
  vlanId: number;
  ip: string;
  subnet: string;
  subnetMask: string;
  dhcp?: boolean;
  //
  dhcpServer?: IDhcpServerSettings;
}

export interface IDhcpServerSettings {
  leaseTime: number;
  //
  poolEnabled: boolean;
  poolStart?: string;
  poolEnd?: string;
  //
  routerIp1?: string;
  routerIp2?: string;
  domainServerIp1?: string;
  domainServerIp2?: string;
  domainName?: string;
  ntpServerIp1?: string;
  ntpServerIp2?: string;
}

export interface IPoE {
  enable: boolean;
  fault: boolean;
  limit: number;
  limit_pwr: number;
  prio: number;
}

export enum ERedundancyProtocol {
  DISABLED = 'disabled',
  RSTP = 'RSTP',
  AUTO = 'Auto',
}

export interface ISTP {
  redundancyProtocol: ERedundancyProtocol;
  bridgePriority: number;
}

/** STP validation errors */
export interface IStpValidationErrors {
  bridgePriority?: string;
}

/** DHCP Server validation errors */
export interface IDhcpServerValidationErrors {
  leaseTime?: string;
  poolStart?: string;
  poolEnd?: string;
  routerIp1?: string;
  routerIp2?: string;
  domainServerIp1?: string;
  domainServerIp2?: string;
  domainName?: string;
  ntpServerIp1?: string;
  ntpServerIp2?: string;
}

/** All possible validation errors for a node. Used by validateNode and stored in node.data.category */
export interface INodeValidationErrors {
  name: string;
  ip: string;
  subnet: string;
  subnetMask: string;
  gateway: string;
  ipInterfaces: {
    ip: string;
    subnet: string;
    subnetMask: string;
  }[];
  stp?: IStpValidationErrors;
  dhcpServer?: IDhcpServerValidationErrors;
}

export interface NodeCategoryModel {
  name: string;
  color: string;
  image?: string;
  hidden?: boolean;
  imageSidebar?: string;
  viewSize: {
    width: number;
    height: number;
  };
  type: EDataType;
  switchType?: EDataSwitchType[];
  ports?: IPort[];
  portIndexes: { [key: string]: number };
  rotation?: number;
  rotatable?: boolean;
  resizable?: boolean;
  //
  ip?: string;
  subnet?: string;
  subnetMask?: string;
  gateway?: string;
  dhcp?: boolean;
  poe?: IPoE;
  stp?: ISTP;
  isValid?: boolean;
  /** Validation error details; set whenever validateNode runs */
  validationErrors?: INodeValidationErrors;
  ipInterfaces?: IpInterface[];
  dhcpServer?: IDhcpServerSettings;
  /** Power cable length in meters */
  powerCableLength?: number;
  /** Free-form notes for documentation export */
  notes?: string;
}

export enum EDataType {
  Group = 'Group',
  Switch = 'Switch',
  Device = 'Device',
  Note = 'Note',
  Image = 'Image',
  ProjectFrame = 'ProjectFrame',
}

export enum EDataSwitchType {
  POE = 'poe',
  MANAGED = 'managed',
  GE = 'gigabit-ethernet',
  FE = 'fast-ethernet',
  UNMANAGED = 'unmanaged',
  P8 = '8-port',
  P10 = '10-port',
  P16 = '16-port',
  P24 = '24-port',
  PUSH_PULL = 'push-pull',
}

export enum EDataTypeColor {
  Switch = '#ffeb3b',
  Device = '#64b5f6',
}

export enum NodeDataType {
  // switches
  Switch006130100 = '006-130-100',
  Switch006130105 = '006-130-105',
  Switch006130106 = '006-130-106',
  Switch006130110 = '006-130-110',
  Switch006130113 = '006-130-113',
  Switch006130114 = '006-130-114',
  Switch006130115 = '006-130-115',
  Switch006130116 = '006-130-116',
  Switch006130117 = '006-130-117',
  Switch006130118 = '006-130-118',
  Switch006130119 = '006-130-119',
  Switch006130120 = '006-130-120',
  Switch006130124 = '006-130-124',
  Switch006130125 = '006-130-125',
  Switch006130126 = '006-130-126',
  Switch006130127 = '006-130-127',
  Switch006130130 = '006-130-130',
  Switch006130131 = '006-130-131',
  Switch006130132 = '006-130-132',
  Switch006130133 = '006-130-133',
  Switch006130140 = '006-130-140',
  // Switch006130141 = '006-130-141',
  Switch006130142 = '006-130-142',
  // Switch006130143 = '006-130-143',
  // camera
  Camera = 'camera',
  // destination-sign
  DestinationSign = 'destination-sign',
  // pcs-sensor
  PCSSensor = 'pcs-sensor',
  // route-display
  RouteDisplay = 'route-display',
  // router
  Router = 'router',
  // validator
  Validator = 'validator',
  // generic device
  GenericDevice = 'generic-device',
  // Recorder
  Recorder = 'recorder',
}

const NodeData: { [key in NodeDataType]: NodeCategoryModel } = {
  [NodeDataType.Switch006130100]: {
    name: '006-130-100',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P8,
      EDataSwitchType.FE,
    ],
    image: './images/switches/006-130-100.png',
    rotatable: true,
    viewSize: {
      width: 728,
      height: 512,
    },
    ports: generatePosition8P(
      '006-130-100',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'],
      [false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
    },
  },
  [NodeDataType.Switch006130105]: {
    name: '006-130-105',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P8,
      EDataSwitchType.GE,
    ],
    image: './images/switches/006-130-105.png',
    rotatable: true,
    viewSize: {
      width: 732,
      height: 518,
    },
    ports: generatePosition8P(
      '006-130-105',
      [
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'],
      [false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
    },
  },
  [NodeDataType.Switch006130106]: {
    name: '006-130-106',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P8,
      EDataSwitchType.FE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-106.png',
    rotatable: true,
    viewSize: {
      width: 727,
      height: 512,
    },
    ports: generatePosition8P(
      '006-130-106',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'],
      [true, true, true, true, true, true, true, true],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
    },
  },
  [NodeDataType.Switch006130110]: {
    name: '006-130-110',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P8,
      EDataSwitchType.GE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-110.png',
    rotatable: true,
    viewSize: {
      width: 728,
      height: 515,
    },
    ports: generatePosition8P(
      '006-130-110',
      [
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'],
      [true, true, true, true, true, true, true, true],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
    },
  },
  [NodeDataType.Switch006130113]: {
    name: '006-130-113',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.FE,
    ],
    image: './images/switches/006-130-113.png',
    rotatable: true,
    viewSize: {
      width: 733,
      height: 517,
    },
    ports: generatePosition10P(
      '006-130-113',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10'],
      [false, false, false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
      P9: 9,
      P10: 10,
    },
  },
  [NodeDataType.Switch006130114]: {
    name: '006-130-114',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.FE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-114.png',
    rotatable: true,
    viewSize: {
      width: 728,
      height: 513,
    },
    ports: generatePosition10P(
      '006-130-114',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10'],
      [false, true, true, true, true, false, true, true, true, true],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
      P9: 9,
      P10: 10,
    },
  },
  [NodeDataType.Switch006130115]: {
    name: '006-130-115',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
    ],
    image: './images/switches/006-130-115.png',
    rotatable: true,
    viewSize: {
      width: 723,
      height: 511,
    },
    ports: generatePosition10P(
      '006-130-115',
      [
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['G1', 'P1', 'P2', 'P3', 'P4', 'G2', 'P5', 'P6', 'P7', 'P8'],
      [false, false, false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      G1: 1,
      P1: 2,
      P2: 3,
      P3: 4,
      P4: 5,
      G2: 6,
      P5: 7,
      P6: 8,
      P7: 9,
      P8: 10,
    },
  },
  [NodeDataType.Switch006130116]: {
    name: '006-130-116',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-116.png',
    rotatable: true,
    viewSize: {
      width: 729,
      height: 515,
    },
    ports: generatePosition10P(
      '006-130-116',
      [
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['G1', 'P1', 'P2', 'P3', 'P4', 'G2', 'P5', 'P6', 'P7', 'P8'],
      [false, true, true, true, true, false, true, true, true, true],
    ),
    portIndexes: {
      G1: 1,
      P1: 2,
      P2: 3,
      P3: 4,
      P4: 5,
      G2: 6,
      P5: 7,
      P6: 8,
      P7: 9,
      P8: 10,
    },
  },
  [NodeDataType.Switch006130117]: {
    name: '006-130-117',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
    ],
    image: './images/switches/006-130-117.png',
    rotatable: true,
    viewSize: {
      width: 727,
      height: 510,
    },
    ports: generatePosition10P(
      '006-130-117',
      [
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['G1', 'P1', 'P2', 'P3', 'P4', 'G2', 'P5', 'P6', 'P7', 'P8'],
      [false, false, false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      G1: 1,
      P1: 2,
      P2: 3,
      P3: 4,
      P4: 5,
      G2: 6,
      P5: 7,
      P6: 8,
      P7: 9,
      P8: 10,
    },
  },
  [NodeDataType.Switch006130118]: {
    name: '006-130-118',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-118.png',
    rotatable: true,
    viewSize: {
      width: 729,
      height: 511,
    },
    ports: generatePosition10P(
      '006-130-118',
      [
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['G1', 'P1', 'P2', 'P3', 'P4', 'G2', 'P5', 'P6', 'P7', 'P8'],
      [false, true, true, true, true, false, true, true, true, true],
    ),
    portIndexes: {
      G1: 1,
      P1: 2,
      P2: 3,
      P3: 4,
      P4: 5,
      G2: 6,
      P5: 7,
      P6: 8,
      P7: 9,
      P8: 10,
    },
  },
  [NodeDataType.Switch006130119]: {
    name: '006-130-119',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P8,
      EDataSwitchType.FE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-119.png',
    rotatable: true,
    viewSize: {
      width: 727,
      height: 513,
    },
    ports: generatePosition8P(
      '006-130-119',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'],
      [true, true, true, true, true, true, true, true],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
    },
  },
  [NodeDataType.Switch006130120]: {
    name: '006-130-120',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P8,
      EDataSwitchType.FE,
    ],
    image: './images/switches/006-130-120.png',
    rotatable: true,
    viewSize: {
      width: 729,
      height: 514,
    },
    ports: generatePosition8P(
      '006-130-120',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'],
      [false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      P7: 7,
      P8: 8,
    },
  },
  [NodeDataType.Switch006130124]: {
    name: '006-130-124',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
      EDataSwitchType.P16,
    ],
    image: './images/switches/006-130-124.png',
    rotatable: true,
    viewSize: {
      width: 918,
      height: 514,
    },
    ports: generatePosition16P(
      '006-130-124',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      [
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'G1',
        'G2',
        'G3',
        'G4',
        'P7',
        'P8',
        'P9',
        'P10',
        'P11',
        'P12',
      ],
      [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      G1: 7,
      P7: 8,
      P8: 9,
      P9: 10,
      P10: 11,
      P11: 12,
      P12: 13,
      G4: 14,
      G2: 15,
      G3: 16,
    },
  },
  [NodeDataType.Switch006130125]: {
    name: '006-130-125',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P16,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-125.png',
    rotatable: true,
    viewSize: {
      width: 927,
      height: 519,
    },
    ports: generatePosition16P(
      '006-130-125',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      [
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'G1',
        'G2',
        'G3',
        'G4',
        'P7',
        'P8',
        'P9',
        'P10',
        'P11',
        'P12',
      ],
      [
        false,
        true,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
        false,
        true,
        true,
        true,
        true,
        false,
      ],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      G1: 7,
      P7: 8,
      P8: 9,
      P9: 10,
      P10: 11,
      P11: 12,
      P12: 13,
      G4: 14,
      G2: 15,
      G3: 16,
    },
  },
  [NodeDataType.Switch006130126]: {
    name: '006-130-126',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    hidden: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P16,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
      EDataSwitchType.PUSH_PULL,
    ],
    image: './images/switches/006-130-126.png',
    rotatable: true,
    viewSize: {
      width: 918,
      height: 512,
    },
    ports: generatePosition16P(
      '006-130-126',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      [
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'G1',
        'G2',
        'G3',
        'G4',
        'P7',
        'P8',
        'P9',
        'P10',
        'P11',
        'P12',
      ],
      [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      G1: 7,
      P7: 8,
      P8: 9,
      P9: 10,
      P10: 11,
      P11: 12,
      P12: 13,
      G4: 14,
      G2: 15,
      G3: 16,
    },
  },
  [NodeDataType.Switch006130127]: {
    name: '006-130-127',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    hidden: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P16,
      EDataSwitchType.POE,
      EDataSwitchType.GE,
      EDataSwitchType.FE,
      EDataSwitchType.PUSH_PULL,
    ],
    image: './images/switches/006-130-127.png',
    rotatable: true,
    viewSize: {
      width: 923,
      height: 512,
    },
    ports: generatePosition16P(
      '006-130-127',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
      ],
      [
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'G1',
        'G2',
        'G3',
        'G4',
        'P7',
        'P8',
        'P9',
        'P10',
        'P11',
        'P12',
      ],
      [
        false,
        true,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
        false,
        true,
        true,
        true,
        true,
        false,
      ],
    ),
    portIndexes: {
      P1: 1,
      P2: 2,
      P3: 3,
      P4: 4,
      P5: 5,
      P6: 6,
      G1: 7,
      P7: 8,
      P8: 9,
      P9: 10,
      P10: 11,
      P11: 12,
      P12: 13,
      G4: 14,
      G2: 15,
      G3: 16,
    },
  },
  [NodeDataType.Switch006130130]: {
    name: '006-130-130',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
    ],
    image: './images/switches/006-130-130.png',
    rotatable: true,
    viewSize: {
      width: 729,
      height: 514,
    },
    ports: generatePosition10P(
      '006-130-130',
      [
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      ['G1', 'P1', 'P2', 'P3', 'P4', 'G2', 'P5', 'P6', 'P7', 'P8'],
      [false, true, true, true, true, false, true, true, true, true],
    ),
    portIndexes: {
      G1: 1,
      P1: 2,
      P2: 3,
      P3: 4,
      P4: 5,
      G2: 6,
      P5: 7,
      P6: 8,
      P7: 9,
      P8: 10,
    },
  },
  [NodeDataType.Switch006130131]: {
    name: '006-130-131',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.UNMANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-131.png',
    rotatable: true,
    viewSize: {
      width: 728,
      height: 513,
    },
    ports: generatePosition10P(
      '006-130-131',
      [
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10'],
      [false, true, true, true, true, false, true, true, true, true],
    ),
    portIndexes: {
      G1: 1,
      G2: 2,
      G3: 3,
      G4: 4,
      G5: 5,
      G6: 6,
      G7: 7,
      G8: 8,
      G9: 9,
      G10: 10,
    },
  },
  [NodeDataType.Switch006130132]: {
    name: '006-130-132',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
    ],
    image: './images/switches/006-130-132.png',
    rotatable: true,
    viewSize: {
      width: 731,
      height: 517,
    },
    ports: generatePosition10P(
      '006-130-132',
      [
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10'],
      [false, false, false, false, false, false, false, false, false, false],
    ),
    portIndexes: {
      G1: 1,
      G2: 2,
      G3: 3,
      G4: 4,
      G5: 5,
      G6: 6,
      G7: 7,
      G8: 8,
      G9: 9,
      G10: 10,
    },
  },
  [NodeDataType.Switch006130133]: {
    name: '006-130-133',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P10,
      EDataSwitchType.GE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-133.png',
    rotatable: true,
    viewSize: {
      width: 732,
      height: 517,
    },
    ports: generatePosition10P(
      '006-130-133',
      [
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10'],
      [false, true, true, true, true, false, true, true, true, true],
    ),
    portIndexes: {
      G1: 1,
      G2: 2,
      G3: 3,
      G4: 4,
      G5: 5,
      G6: 6,
      G7: 7,
      G8: 8,
      G9: 9,
      G10: 10,
    },
  },
  [NodeDataType.Switch006130140]: {
    name: '006-130-140',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P24,
      EDataSwitchType.FE,
      EDataSwitchType.GE,
      EDataSwitchType.POE,
    ],
    image: './images/switches/006-130-140.png',
    rotatable: true,
    viewSize: {
      width: 1419,
      height: 518,
    },
    ports: generatePosition24P(
      '006-130-140',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      [
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'P7',
        'P8',
        'P9',
        'P10',
        'P11',
        'P12',
        'P13',
        'P14',
        'P15',
        'P16',
        'P17',
        'P18',
        'P19',
        'P20',
        'G1',
        'G2',
        'G3',
        'G4',
      ],
      [
        false,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        false,
        false,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
      ],
    ),
    portIndexes: {
      G1: 1,
      G2: 2,
      G3: 3,
      G4: 4,
      P1: 5,
      P2: 6,
      P3: 7,
      P4: 8,
      P5: 9,
      P6: 10,
      P7: 11,
      P8: 12,
      P9: 13,
      P10: 14,
      P11: 15,
      P12: 16,
      P13: 17,
      P14: 18,
      P15: 19,
      P16: 20,
      P17: 21,
      P18: 22,
      P19: 23,
      P20: 24,
    },
  },
  // [NodeDataType.Switch006130141]: {
  //   name: '006-130-141',
  //   color: EDataTypeColor.Switch,
  //   type: EDataType.Switch,
  //   dhcp: true,
  //   switchType: [
  //     EDataSwitchType.MANAGED,
  //     EDataSwitchType.P24,
  //     EDataSwitchType.FE,
  //     EDataSwitchType.GE,
  //     EDataSwitchType.POE,
  //     EDataSwitchType.PUSH_PULL,
  //   ],
  //   image: './images/switches/006-130-141.png',
  //   rotatable: true,
  //   viewSize: {
  //     width: 1419,
  //     height: 512,
  //   },
  //   ports: generatePosition24P(
  //     '006-130-141',
  //     [
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.GE,
  //       EPortConnectionType.GE,
  //       EPortConnectionType.GE,
  //       EPortConnectionType.GE,
  //     ],
  //     [
  //       'P1',
  //       'P2',
  //       'P3',
  //       'P4',
  //       'P5',
  //       'P6',
  //       'P7',
  //       'P8',
  //       'P9',
  //       'P10',
  //       'P11',
  //       'P12',
  //       'P13',
  //       'P14',
  //       'P15',
  //       'P16',
  //       'P17',
  //       'P18',
  //       'P19',
  //       'P20',
  //       'G1',
  //       'G2',
  //       'G3',
  //       'G4',
  //     ],
  //     [
  //       false,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       false,
  //       false,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       false,
  //       false,
  //       false,
  //       false,
  //       false,
  //     ],
  //   ),
  //   portIndexes: {
  //     G1: 1,
  //     G2: 2,
  //     G3: 3,
  //     G4: 4,
  //     P1: 5,
  //     P2: 6,
  //     P3: 7,
  //     P4: 8,
  //     P5: 9,
  //     P6: 10,
  //     P7: 11,
  //     P8: 12,
  //     P9: 13,
  //     P10: 14,
  //     P11: 15,
  //     P12: 16,
  //     P13: 17,
  //     P14: 18,
  //     P15: 19,
  //     P16: 20,
  //     P17: 21,
  //     P18: 22,
  //     P19: 23,
  //     P20: 24,
  //   },
  // },
  [NodeDataType.Switch006130142]: {
    name: '006-130-142',
    color: EDataTypeColor.Switch,
    type: EDataType.Switch,
    dhcp: true,
    switchType: [
      EDataSwitchType.MANAGED,
      EDataSwitchType.P24,
      EDataSwitchType.FE,
      EDataSwitchType.GE,
    ],
    image: './images/switches/006-130-142.png',
    rotatable: true,
    viewSize: {
      width: 1419,
      height: 518,
    },
    ports: generatePosition24P(
      '006-130-142',
      [
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.FE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
        EPortConnectionType.GE,
      ],
      [
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'P7',
        'P8',
        'P9',
        'P10',
        'P11',
        'P12',
        'P13',
        'P14',
        'P15',
        'P16',
        'P17',
        'P18',
        'P19',
        'P20',
        'G1',
        'G2',
        'G3',
        'G4',
      ],
      [
        false,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        false,
        false,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        false,
        false,
        false,
        false,
        false,
      ],
    ),
    portIndexes: {
      G1: 1,
      G2: 2,
      G3: 3,
      G4: 4,
      P1: 5,
      P2: 6,
      P3: 7,
      P4: 8,
      P5: 9,
      P6: 10,
      P7: 11,
      P8: 12,
      P9: 13,
      P10: 14,
      P11: 15,
      P12: 16,
      P13: 17,
      P14: 18,
      P15: 19,
      P16: 20,
      P17: 21,
      P18: 22,
      P19: 23,
      P20: 24,
    },
  },
  // [NodeDataType.Switch006130143]: {
  //   name: '006-130-143',
  //   color: EDataTypeColor.Switch,
  //   type: EDataType.Switch,
  //   dhcp: true,
  //   switchType: [
  //     EDataSwitchType.MANAGED,
  //     EDataSwitchType.P24,
  //     EDataSwitchType.FE,
  //     EDataSwitchType.GE,
  //     EDataSwitchType.PUSH_PULL,
  //   ],
  //   image: './images/switches/006-130-143.png',
  //   rotatable: true,
  //   viewSize: {
  //     width: 1419,
  //     height: 512,
  //   },
  //   ports: generatePosition24P(
  //     '006-130-143',
  //     [
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.FE,
  //       EPortConnectionType.GE,
  //       EPortConnectionType.GE,
  //       EPortConnectionType.GE,
  //       EPortConnectionType.GE,
  //     ],
  //     [
  //       'P1',
  //       'P2',
  //       'P3',
  //       'P4',
  //       'P5',
  //       'P6',
  //       'P7',
  //       'P8',
  //       'P9',
  //       'P10',
  //       'P11',
  //       'P12',
  //       'P13',
  //       'P14',
  //       'P15',
  //       'P16',
  //       'P17',
  //       'P18',
  //       'P19',
  //       'P20',
  //       'G1',
  //       'G2',
  //       'G3',
  //       'G4',
  //     ],
  //     [
  //       false,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       false,
  //       false,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       true,
  //       false,
  //       false,
  //       false,
  //       false,
  //       false,
  //     ],
  //   ),
  //   portIndexes: {
  //     G1: 1,
  //     G2: 2,
  //     G3: 3,
  //     G4: 4,
  //     P1: 5,
  //     P2: 6,
  //     P3: 7,
  //     P4: 8,
  //     P5: 9,
  //     P6: 10,
  //     P7: 11,
  //     P8: 12,
  //     P9: 13,
  //     P10: 14,
  //     P11: 15,
  //     P12: 16,
  //     P13: 17,
  //     P14: 18,
  //     P15: 19,
  //     P16: 20,
  //     P17: 21,
  //     P18: 22,
  //     P19: 23,
  //     P20: 24,
  //   },
  // },
  [NodeDataType.Camera]: {
    name: 'Camera',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/camera.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'camera',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Camera',
          animated: false,
        },
      },
    ],
    portIndexes: {
      camera: 1,
    },
  },
  [NodeDataType.DestinationSign]: {
    name: 'Destination Sign',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/destination-sign.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'dest-sign',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Destination Sign',
          animated: false,
        },
      },
    ],
    portIndexes: {
      'dest-sign': 1,
    },
  },
  [NodeDataType.PCSSensor]: {
    name: 'Passenger Counting Sensor',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/pcs-sensor.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'pcs-sensor',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Passenger Counting Sensor',
          animated: false,
        },
      },
    ],
    portIndexes: {
      'pcs-sensor': 1,
    },
  },
  [NodeDataType.RouteDisplay]: {
    name: 'Route Display',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/route-display.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'route-display',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Route Display',
          animated: false,
        },
      },
    ],
    portIndexes: {
      'route-display': 1,
    },
  },
  [NodeDataType.Router]: {
    name: 'Router',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/router.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'router',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Router',
          animated: false,
        },
      },
    ],
    portIndexes: {
      router: 1,
    },
  },
  [NodeDataType.Validator]: {
    name: 'Validator',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/validator.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'validator',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Validator',
          animated: false,
        },
      },
    ],
    portIndexes: {
      validator: 1,
    },
  },
  [NodeDataType.GenericDevice]: {
    name: 'Generic Device',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/generic-device.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'generic-device',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Generic Device',
          animated: false,
        },
      },
    ],
    portIndexes: {
      'generic-device': 1,
    },
  },
  [NodeDataType.Recorder]: {
    name: 'Recorder',
    color: EDataTypeColor.Device,
    type: EDataType.Device,
    image: './images/devices/recorder.png',
    viewSize: {
      width: 400,
      height: 400,
    },
    resizable: true,
    rotatable: true,
    ports: [
      {
        id: 'recorder',
        type: [EPortType.SOURCE],
        connectionType: EPortConnectionType.MIXED,
        position: Position.Top,
        edgeData: {
          // type: EEdgeTypes.SMOOTHSTEP,
          // label: 'Recorder',
          animated: false,
        },
      },
    ],
    portIndexes: {
      recorder: 1,
    },
  },
};

export default NodeData;
